import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useLang } from "@/context/LanguageContext";
import { ShieldCheck, Truck, Ticket, Building2, User } from "lucide-react";
import RegistrationForm from "@/components/RegistrationForm";

/**
 * Registration page with a Cliente / Fornitore toggle.
 */
export default function CustomerRegister() {
  const { customer } = useCustomerAuth();
  const { t } = useLang();
  const [role, setRole] = useState("customer"); // "customer" | "supplier"
  if (customer) return <Navigate to="/account" replace />;

  return (
    <div className="min-h-screen bg-xd-light" data-testid="page-register">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[1fr_360px] gap-10 items-start">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyanHover">{t("auth_kicker_register")}</div>
          <h1 className="font-display text-3xl font-semibold mt-2">{t("auth_register_title")}</h1>
          <p className="text-sm text-xd-muted mt-2 max-w-lg">{t("auth_register_desc")}</p>

          {/* Role toggle */}
          <div className="mt-6 inline-flex bg-white border border-xd-border rounded-lg p-1" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={role === "customer"}
              onClick={() => setRole("customer")}
              data-testid="role-toggle-customer"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${role === "customer" ? "bg-xd-cyan text-xd-dark" : "text-xd-muted hover:text-xd-text"}`}
            >
              <User className="w-4 h-4" /> {t("auth_role_customer")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={role === "supplier"}
              onClick={() => setRole("supplier")}
              data-testid="role-toggle-supplier"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${role === "supplier" ? "bg-xd-cyan text-xd-dark" : "text-xd-muted hover:text-xd-text"}`}
            >
              <Building2 className="w-4 h-4" /> {t("auth_role_supplier")}
            </button>
          </div>

          <RegistrationForm role={role} />

          <div className="text-center text-sm text-xd-muted mt-4">
            {t("auth_have_account")} <Link to="/login" className="text-xd-cyanHover font-semibold hover:underline">{t("auth_login_link")}</Link>
          </div>
        </div>

        <aside className="bg-xd-dark text-white rounded-xl p-6 lg:sticky lg:top-24" data-testid="register-benefits">
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyan">{t("auth_benefits_title")}</div>
          <div className="font-display text-lg font-semibold mt-1 mb-4">
            {role === "supplier" ? t("auth_benefits_supplier") : t("auth_benefits_customer")}
          </div>
          {role === "supplier" ? (
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3"><Ticket className="w-4 h-4 text-xd-cyan mt-0.5" /><span><strong className="text-white">{t("auth_ben_supp_1_t")}</strong><br/><span className="text-slate-400 text-xs">{t("auth_ben_supp_1_d")}</span></span></li>
              <li className="flex gap-3"><ShieldCheck className="w-4 h-4 text-xd-cyan mt-0.5" /><span><strong className="text-white">{t("auth_ben_supp_2_t")}</strong><br/><span className="text-slate-400 text-xs">{t("auth_ben_supp_2_d")}</span></span></li>
              <li className="flex gap-3"><Truck className="w-4 h-4 text-xd-cyan mt-0.5" /><span><strong className="text-white">{t("auth_ben_supp_3_t")}</strong><br/><span className="text-slate-400 text-xs">{t("auth_ben_supp_3_d")}</span></span></li>
            </ul>
          ) : (
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3"><Truck className="w-4 h-4 text-xd-cyan mt-0.5" /><span><strong className="text-white">{t("auth_ben_cust_1_t")}</strong><br/><span className="text-slate-400 text-xs">{t("auth_ben_cust_1_d")}</span></span></li>
              <li className="flex gap-3"><ShieldCheck className="w-4 h-4 text-xd-cyan mt-0.5" /><span><strong className="text-white">{t("auth_ben_cust_2_t")}</strong><br/><span className="text-slate-400 text-xs">{t("auth_ben_cust_2_d")}</span></span></li>
              <li className="flex gap-3"><Ticket className="w-4 h-4 text-xd-cyan mt-0.5" /><span><strong className="text-white">{t("auth_ben_cust_3_t")}</strong><br/><span className="text-slate-400 text-xs">{t("auth_ben_cust_3_d")}</span></span></li>
            </ul>
          )}
        </aside>
      </div>
      <Footer />
    </div>
  );
}
